import axios from 'axios'
import { API_ERROR_MESSAGES } from '@/constants/errorMessages'
import { coverageMessageForCode } from '@/utils/coverageErrorMessages'

/**
 * Shape tolerante do body de erro da API — cobre o formato legado
 * (`message` string/array) e o novo (`code`/`errorCode`/`statusCode`).
 */
export interface ApiErrorBody {
  message?: string | string[]
  code?: string
  errorCode?: string
  statusCode?: number
  [k: string]: unknown
}

interface ErrorLikeWithResponse {
  code?: string
  response?: {
    status?: number
    data?: ApiErrorBody
  }
}

/**
 * Erro JÁ normalizado por `Service.handleError`: a `message` passou pelo funil
 * de `getApiErrorMessage` e é exibível ao usuário. Existe para que um erro
 * relançado pelo store chegue ao `notifyError` do componente sem perder a
 * mensagem específica — um `Error` cru (TypeError, bug de código) continua
 * caindo no fallback, para nunca vazar texto técnico.
 */
export class ServiceError extends Error {
  readonly originalError: unknown

  constructor(message: string, originalError?: unknown) {
    super(message)
    this.name = 'ServiceError'
    this.originalError = originalError
    // Target ES2015 + subclasse de Error: garante o protótipo para `instanceof`.
    Object.setPrototypeOf(this, ServiceError.prototype)
  }
}

export function isServiceError(error: unknown): error is ServiceError {
  return error instanceof ServiceError
}

/**
 * Devolve o erro HTTP original quando `error` é um `ServiceError` (senão o
 * próprio). Guards de status/código aplicados a um erro relançado pelo store
 * precisam olhar o erro embrulhado, não o wrapper.
 */
export function unwrapApiError(error: unknown): unknown {
  return isServiceError(error) ? error.originalError : error
}

function getErrorBody(error: unknown): ApiErrorBody | undefined {
  return (unwrapApiError(error) as ErrorLikeWithResponse | null | undefined)?.response?.data
}

function getErrorCode(error: unknown): string | undefined {
  const code = (unwrapApiError(error) as ErrorLikeWithResponse | null | undefined)?.code
  return typeof code === 'string' ? code : undefined
}

/** True para timeout de request (axios `ECONNABORTED`/`ETIMEDOUT`). */
export function isTimeoutError(error: unknown): boolean {
  const code = getErrorCode(error)
  return code === 'ECONNABORTED' || code === 'ETIMEDOUT'
}

/** True para falha de rede sem resposta do servidor (axios `ERR_NETWORK`). */
export function isNetworkError(error: unknown): boolean {
  return getErrorCode(error) === 'ERR_NETWORK'
}

/** Status HTTP do erro, de `response.status` ou do `statusCode` do body. */
export function getApiErrorStatus(error: unknown): number | undefined {
  const source = unwrapApiError(error)
  if (axios.isAxiosError(source) && typeof source.response?.status === 'number') {
    return source.response.status
  }
  const errorLike = source as ErrorLikeWithResponse | null | undefined
  const status = errorLike?.response?.status
  if (typeof status === 'number') return status
  const statusCode = getErrorBody(source)?.statusCode
  return typeof statusCode === 'number' ? statusCode : undefined
}

/**
 * True quando o erro carrega o código dado — em `data.code`, `data.errorCode`
 * ou (transição legada) como `data.message` string.
 */
export function hasApiErrorCode(error: unknown, code: string): boolean {
  const body = getErrorBody(error)
  if (!body) return false
  return body.code === code || body.errorCode === code || body.message === code
}

/**
 * `details` estruturado do body (aditivo da `DomainException` do backend), para a
 * tela reagir ao erro — ex.: o id do registro em que a ação pode ser refeita.
 */
export function getApiErrorDetails(error: unknown): Record<string, unknown> | undefined {
  const details = getErrorBody(error)?.details
  return details && typeof details === 'object' && !Array.isArray(details)
    ? (details as Record<string, unknown>)
    : undefined
}

/**
 * Extrai a mensagem do body do erro, considerando array ou string.
 */
function extractMessageFromBody(error: unknown, fallback: string): string {
  const message = getErrorBody(error)?.message
  if (Array.isArray(message)) return message.join(', ') || fallback
  if (typeof message === 'string' && message) return message
  return fallback
}

/**
 * Extrai a mensagem de erro exibível ao usuário, em funil:
 * 0. `ServiceError` (já normalizado pelo service) → sua própria message;
 * 1. timeout → mensagem genérica de timeout;
 * 2. falha de rede → mensagem genérica de conexão;
 * 3. HTTP 5xx → mensagem genérica de servidor (nunca repassa message técnico);
 * 4. código do "Ocupado sem identificação" → frase do catálogo pt-BR;
 * 5. senão, message do backend (array vira lista separada por vírgula);
 *    vazio/ausente → `fallback`.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (isServiceError(error)) return error.message || fallback
  if (isTimeoutError(error)) return API_ERROR_MESSAGES.TIMEOUT_ERROR
  if (isNetworkError(error)) return API_ERROR_MESSAGES.NETWORK_ERROR

  const status = getApiErrorStatus(error)
  if (typeof status === 'number' && status >= 500) return API_ERROR_MESSAGES.SERVER_ERROR

  // "Ocupado sem identificação": frase estável do catálogo pt-BR, venha de onde vier.
  const body = getErrorBody(error)
  const coverageMessage =
    coverageMessageForCode(body?.code) ?? coverageMessageForCode(body?.errorCode)
  if (coverageMessage) return coverageMessage

  return extractMessageFromBody(error, fallback)
}
